import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnchorId } from '../../../core/Enums';
import ManagerLayout from '../../../mimic/manager/ManagerLayout';
import { useTour } from '../../../core/TourProvider';
import { PaymentsStepId } from '../../../core/PaymentsEnums';
import { useTourAnchor } from '../../../hooks/useTourAnchor';
import BasicHeaderFields from '../../../components/BasicHeaderFields';
import BasicHeaderTabs from '../../../components/BasicHeaderTabs';
import TransactionPanel from '../../../components/TransactionPanel';
import styles from '../../../stylesheets/managerOutstandingScene.module.scss';

type ManagerTabId = 'unit' | 'transactions' | 'messages';

const ManagerTransactionsScene = () => {
  const { model, goTo, next, stepId } = useTour();
  const [activeTab, setActiveTab] = useState<ManagerTabId>('transactions');
  const rentPaymentRef = useTourAnchor(
    AnchorId.MANAGER_TRANSACTIONS_RENT_PAYMENT
  );
  const completionTimerRef = useRef<number | null>(null);
  const hasQueuedCompletionRef = useRef(false);
  const unitLabel = model.tenant?.unitName ?? 'Unit';
  const invoices = model.payments?.invoices ?? [];
  const transactionRows = model.payments?.transactions ?? [];
  const paidSourceInvoice =
    invoices.find((invoice) => invoice.status === 'outstanding') ?? invoices[0];

  const handleTabChange = (tabId: ManagerTabId) => {
    if (tabId === 'unit') {
      goTo(PaymentsStepId.MANAGER_CLEARED);
      return;
    }
    setActiveTab(tabId);
  };

  const queueTourCompletion = useCallback(() => {
    if (stepId !== PaymentsStepId.MANAGER_TRANSACTIONS) {
      return;
    }
    if (hasQueuedCompletionRef.current) {
      return;
    }
    hasQueuedCompletionRef.current = true;
    completionTimerRef.current = window.setTimeout(() => {
      completionTimerRef.current = null;
      next();
    }, 2000);
  }, [next, stepId]);

  useEffect(() => {
    if (stepId !== PaymentsStepId.MANAGER_TRANSACTIONS) {
      return;
    }
    hasQueuedCompletionRef.current = false;
    if (completionTimerRef.current) {
      window.clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  }, [stepId]);

  useEffect(() => {
    return () => {
      if (completionTimerRef.current) {
        window.clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, []);

  const paymentTransaction = useMemo(() => {
    if (!paidSourceInvoice) {
      return (
        transactionRows.find(
          (transaction) => transaction.kind === 'invoicePayment'
        ) ?? null
      );
    }

    const invoiceNumber = String(
      paidSourceInvoice.docNo ?? paidSourceInvoice.id
    );
    const firstLineItem = paidSourceInvoice.lineItems[0];
    const appliedTitle = firstLineItem?.site ?? unitLabel;
    const appliedItemType =
      firstLineItem?.itemType ?? firstLineItem?.itemName ?? 'Rent';
    const appliedTo = paidSourceInvoice.docNo
      ? `Rent #${paidSourceInvoice.docNo}`
      : 'Rent';

    const paidPaymentTransaction = {
      id: `payment-${paidSourceInvoice.id}`,
      kind: 'invoicePayment' as const,
      date: paidSourceInvoice.dueDate,
      number: invoiceNumber,
      amount: -paidSourceInvoice.total,
      outstanding: 0,
      balance: 0,
      statusLabel: 'Applied',
      appliedItems: [
        {
          id: `applied-${paidSourceInvoice.id}`,
          title: appliedTitle,
          amount: paidSourceInvoice.total,
          quantity: firstLineItem?.quantity ?? 1,
          itemType: appliedItemType,
          appliedTo,
          bankAccount: 'Bank of America',
        },
      ],
    };

    return paidPaymentTransaction;
  }, [paidSourceInvoice, transactionRows, unitLabel]);

  return (
    <ManagerLayout>
      <BasicHeaderFields balanceAmount={0} />
      <BasicHeaderTabs
        activeId={activeTab}
        unitLabel={unitLabel}
        onChange={handleTabChange}
      />
      <div className={styles.contentSection}>
        {activeTab === 'transactions' ? (
          <div className={styles.transactionsList}>
            {paymentTransaction ? (
              <div className={styles.featuredTransactions}>
                <div ref={rentPaymentRef}>
                  <TransactionPanel
                    transaction={paymentTransaction}
                    onInteraction={queueTourCompletion}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className={styles.transactionsPlaceholder}>
            <h2 className={styles.transactionsTitle}>Messages</h2>
            <p className={styles.transactionsBody}>
              This is where you would see and send messages to your tenants.
            </p>
          </div>
        )}
      </div>
    </ManagerLayout>
  );
};

export default ManagerTransactionsScene;
